import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, KanbanSquare, CheckSquare, Search as SearchIcon,
  Settings as SettingsIcon, LogOut, Sun, Moon, Menu, X, FileText,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { initialsOf } from "@/lib/crm";
import { BRAND } from "@/lib/constants";

const NAV = [
  { to: "/crm", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/crm/leads", label: "Leads", icon: Users },
  { to: "/crm/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/crm/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/crm/insights", label: "Insights", icon: FileText },
  { to: "/crm/search", label: "Search", icon: SearchIcon },
  { to: "/crm/settings", label: "Settings", icon: SettingsIcon },
];

export default function CrmLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/crm/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-neutral-900 dark:text-neutral-50 font-sans">
      {/* Sidebar */}
      <aside
        data-testid="crm-sidebar"
        className={`fixed inset-y-0 left-0 z-40 w-60 bg-neutral-50 dark:bg-neutral-950 border-r border-neutral-200 dark:border-neutral-800 flex flex-col
        transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="h-16 flex items-center px-5 border-b border-neutral-200 dark:border-neutral-800">
          <div className="h-10 overflow-hidden flex items-center">
            <img src={theme === "dark" ? BRAND.logoWhite : BRAND.logoBlack} alt="Velor Advisory CRM" className="h-[90px] w-auto object-contain" />
          </div>
          <span className="ml-2 text-[10px] tracking-overline uppercase text-neutral-500">CRM</span>
        </div>

        <nav className="flex-1 py-4 px-2 space-y-1" data-testid="crm-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileOpen(false)}
              data-testid={`crm-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-neutral-900 text-white dark:bg-white dark:text-black"
                    : "text-neutral-700 hover:bg-neutral-200/60 dark:text-neutral-300 dark:hover:bg-neutral-800/60"
                }`
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-neutral-200 dark:border-neutral-800 p-3 space-y-2">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black flex items-center justify-center text-xs font-semibold overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt={`${user?.name || "User"} profile photo`} className="h-full w-full object-cover" />
              ) : (
                initialsOf(user?.name || user?.email)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="crm-user-name">{user?.name || user?.email}</div>
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 truncate">{user?.role}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 px-1">
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-neutral-200 dark:border-neutral-800 text-xs tracking-overline uppercase hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              title="Toggle theme"
            >
              {theme === "dark" ? <Sun size={14}/> : <Moon size={14}/>}
              {theme === "dark" ? "Light" : "Dark"}
            </button>
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-neutral-200 dark:border-neutral-800 text-xs tracking-overline uppercase hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <LogOut size={14}/> Log out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-white dark:bg-black border-b border-neutral-200 dark:border-neutral-800 z-30 flex items-center justify-between px-4">
        <button onClick={() => setMobileOpen((v) => !v)} data-testid="crm-mobile-menu-toggle" aria-label="Toggle menu">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <div className="text-[11px] tracking-overline uppercase">Velor CRM</div>
        <div className="w-6" />
      </div>

      {/* Main */}
      <main className="lg:pl-60 pt-14 lg:pt-0">
        <div className="min-h-screen">
          {children}
        </div>
      </main>
    </div>
  );
}
